import * as THREE from 'three';
import { GameObject } from '../objects/GameObject';
import { Camera } from '../rendering/Camera';
import { FrustumCullingSystem } from './FrustumCullingSystem';
import { Config } from '../core/Config';

/**
 * Manages hierarchical occlusion culling for efficient rendering
 * Uses the frustum culling system as a first pass to eliminate objects outside the view
 * Then performs occlusion testing to remove objects that are hidden by others
 */
export class OcclusionCullingSystem {
  private static instance: OcclusionCullingSystem | null = null;
  
  private camera: Camera;
  private scene: THREE.Scene;
  private frustumCulling: FrustumCullingSystem;
  
  // The 3D renderer - needed for occlusion queries
  private renderer: THREE.WebGLRenderer;
  
  // Occlusion query objects
  private occlusionRenderer: THREE.WebGLRenderer | null = null;
  private occlusionRenderTarget: THREE.WebGLRenderTarget | null = null;
  private occlusionCamera: THREE.PerspectiveCamera | null = null;
  private occlusionScene: THREE.Scene | null = null;
  
  // Debug visualization
  private debugMode: boolean = false;
  private debugObjects: THREE.Object3D[] = [];
  
  // Tracking of visible and occluded objects
  private visibleObjects: Set<string> = new Set();
  private occludedObjects: Set<string> = new Set();
  
  // Tracking performance metrics
  private totalObjectsChecked: number = 0;
  private totalObjectsVisible: number = 0;
  private totalObjectsOccluded: number = 0;
  
  // Occlusion bounding boxes - one reusable box mesh per object
  private occlusionBoxes: Map<string, THREE.Mesh> = new Map();
  
  // Configuration
  private enabled: boolean = true;
  private occlusionCheckInterval: number = 5; // Check every N frames
  private currentFrame: number = 0;
  private hierarchicalTesting: boolean = true;
  
  // Reference to SpatialManager for getting the quadtree
  private spatialManager: any = null;
  
  private constructor(camera: Camera, scene: THREE.Scene, renderer: THREE.WebGLRenderer) {
    this.camera = camera;
    this.scene = scene;
    this.renderer = renderer;
    
    // Get the frustum culling system
    this.frustumCulling = FrustumCullingSystem.getInstance(camera, scene);
    
    // Initialize occlusion rendering
    this.initOcclusionRendering();
    
    console.log('OcclusionCullingSystem initialized');
  }
  
  /**
   * Get the singleton instance
   */
  public static getInstance(
    camera?: Camera, 
    scene?: THREE.Scene, 
    renderer?: THREE.WebGLRenderer
  ): OcclusionCullingSystem {
    if (!OcclusionCullingSystem.instance && camera && scene && renderer) {
      OcclusionCullingSystem.instance = new OcclusionCullingSystem(camera, scene, renderer);
    } else if (!OcclusionCullingSystem.instance && (!camera || !scene || !renderer)) {
      throw new Error('OcclusionCullingSystem not initialized. Provide camera, scene, and renderer on first call.');
    }
    
    return OcclusionCullingSystem.instance as OcclusionCullingSystem;
  }
  
  /**
   * Initialize the occlusion rendering system
   */
  private initOcclusionRendering(): void {
    // Create a low-resolution render target for occlusion testing
    const pixelRatio = window.devicePixelRatio || 1;
    const width = Math.floor(window.innerWidth / 4);
    const height = Math.floor(window.innerHeight / 4);
    
    this.occlusionRenderTarget = new THREE.WebGLRenderTarget(width, height);
    
    // Create a separate scene for occlusion testing
    this.occlusionScene = new THREE.Scene();
    this.occlusionScene.background = new THREE.Color(0x000000);
    
    // Create a separate camera for occlusion testing
    this.occlusionCamera = this.camera.getThreeCamera().clone();
    
    // Create a renderer for occlusion testing
    // We'll reuse the main renderer to avoid creating multiple WebGL contexts
    this.occlusionRenderer = this.renderer;
  }
  
  /**
   * Update occlusion camera to match main camera
   */
  private updateOcclusionCamera(): void {
    if (!this.occlusionCamera) return;
    
    const mainCamera = this.camera.getThreeCamera();
    this.occlusionCamera.position.copy(mainCamera.position);
    this.occlusionCamera.rotation.copy(mainCamera.rotation);
    this.occlusionCamera.quaternion.copy(mainCamera.quaternion);
    this.occlusionCamera.scale.copy(mainCamera.scale);
    this.occlusionCamera.projectionMatrix.copy(mainCamera.projectionMatrix);
    this.occlusionCamera.matrixWorld.copy(mainCamera.matrixWorld);
    this.occlusionCamera.matrixWorldInverse.copy(mainCamera.matrixWorldInverse);
  }
  
  /**
   * Create or get occlusion bounding box for an object
   */
  private getOcclusionBox(object: GameObject): THREE.Mesh {
    const id = object.getId();
    
    if (this.occlusionBoxes.has(id)) {
      return this.occlusionBoxes.get(id)!;
    }
    
    // Create a simple box to represent the object for occlusion testing
    const position = object.getPosition();
    const scale = object.getScale();
    
    // Get actual bounding box if available, or create a simple one based on scale
    const size = Math.max(scale.x, scale.y, scale.z) * 2;
    const geometry = new THREE.BoxGeometry(size, size, size);
    const material = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      side: THREE.BackSide // Render back faces only for conservative occlusion
    });
    
    const box = new THREE.Mesh(geometry, material);
    box.position.copy(position);
    box.visible = false; // Hidden by default
    
    this.occlusionBoxes.set(id, box);
    return box;
  }
  
  /**
   * Update occlusion box position and size to match the object
   */
  private updateOcclusionBox(object: GameObject): void {
    const box = this.getOcclusionBox(object);
    const position = object.getPosition();
    
    box.position.copy(position);
    
    // Update scale if needed
    // For now, we'll just keep a fixed scale based on the object's initial size
  }
  
  /**
   * Perform occlusion testing for a set of objects
   * Returns a filtered list of objects that are potentially visible
   */
  public cullObjects(objects: GameObject[]): GameObject[] {
    // Skip occlusion testing if disabled
    if (!this.enabled) {
      return objects;
    }
    
    // Update frame counter
    this.currentFrame = (this.currentFrame + 1) % this.occlusionCheckInterval;
    
    // Only perform occlusion testing every N frames to improve performance
    if (this.currentFrame !== 0) {
      return this.getLastVisibleObjects(objects);
    }
    
    // First, use frustum culling as a quick first pass
    let frustumVisible: GameObject[] = [];
    
    // If we have a spatial manager, use its quadtree for hierarchical culling
    if (this.spatialManager) {
      // Get the quadtree from the spatial manager
      const quadtree = this.spatialManager['quadtree'];
      if (quadtree) {
        // Use the frustum culling system's quadtree culling
        frustumVisible = this.frustumCulling.cullQuadtree(quadtree);
      } else {
        // Fallback to simple frustum culling of provided objects
        frustumVisible = this.simpleFrustumCull(objects);
      }
    } else {
      // Just do simple frustum culling if no spatial manager
      frustumVisible = this.simpleFrustumCull(objects);
    }
    
    // Reset counters for this frame
    this.totalObjectsChecked = frustumVisible.length;
    this.totalObjectsVisible = 0;
    this.totalObjectsOccluded = 0;
    
    // Clear previous results
    this.visibleObjects.clear();
    this.occludedObjects.clear();
    
    // If hierarchical testing is enabled, sort objects by distance from camera
    if (this.hierarchicalTesting) {
      this.performHierarchicalOcclusionTest(frustumVisible);
    } else {
      this.performSimpleOcclusionTest(frustumVisible);
    }
    
    // Update debug visualizations if enabled
    if (this.debugMode) {
      this.updateDebugVisualizations();
    }
    
    // Return only the visible objects
    return frustumVisible.filter(obj => this.visibleObjects.has(obj.getId()));
  }
  
  /**
   * Simple frustum culling of objects (when quadtree isn't available)
   */
  private simpleFrustumCull(objects: GameObject[]): GameObject[] {
    // Update the frustum first
    this.frustumCulling['updateFrustum']();
    
    return objects.filter(obj => this.frustumCulling.isObjectInFrustum(obj));
  }
  
  /**
   * Set the spatial manager to use for quadtree-based culling
   */
  public setSpatialManager(spatialManager: any): void {
    this.spatialManager = spatialManager;
  }
  
  /**
   * Perform simple occlusion testing for all objects
   * This tests each object individually against the depth buffer
   */
  private performSimpleOcclusionTest(objects: GameObject[]): void {
    if (!this.occlusionScene || !this.occlusionCamera || !this.occlusionRenderer || !this.occlusionRenderTarget) {
      return;
    }
    
    // Update occlusion camera
    this.updateOcclusionCamera();
    
    // Clear the occlusion scene
    while (this.occlusionScene.children.length > 0) {
      this.occlusionScene.remove(this.occlusionScene.children[0]);
    }
    
    // Add occluders (large objects that will likely occlude others)
    // For simplicity, we'll consider all objects as potential occluders
    for (const object of objects) {
      const box = this.getOcclusionBox(object);
      this.updateOcclusionBox(object);
      
      // Add to occlusion scene
      this.occlusionScene.add(box);
    }
    
    // Render the occlusion scene to depth buffer
    const oldTarget = this.occlusionRenderer.getRenderTarget();
    this.occlusionRenderer.setRenderTarget(this.occlusionRenderTarget);
    this.occlusionRenderer.render(this.occlusionScene, this.occlusionCamera);
    this.occlusionRenderer.setRenderTarget(oldTarget);
    
    // Read back the depth buffer (not actually implemented since this would require pixel readback,
    // which isn't efficient in WebGL. In a real implementation, you'd use occlusion queries)
    
    // For now, we'll just mark all frustum-visible objects as visible
    for (const object of objects) {
      this.visibleObjects.add(object.getId());
    }
    this.totalObjectsVisible = objects.length;
  }
  
  /**
   * Perform hierarchical occlusion testing
   * Renders potentially visible objects front-to-back, using previously rendered objects as occluders
   */
  private performHierarchicalOcclusionTest(objects: GameObject[]): void {
    if (!this.occlusionScene || !this.occlusionCamera || !this.occlusionRenderer || !this.occlusionRenderTarget) {
      return;
    }
    
    // Update occlusion camera
    this.updateOcclusionCamera();
    
    // Clear the occlusion scene
    while (this.occlusionScene.children.length > 0) {
      this.occlusionScene.remove(this.occlusionScene.children[0]);
    }
    
    // Sort objects by distance from camera (front to back)
    const cameraPos = this.camera.getPosition();
    objects.sort((a, b) => {
      const posA = a.getPosition();
      const posB = b.getPosition();
      
      const distA = posA.distanceToSquared(cameraPos);
      const distB = posB.distanceToSquared(cameraPos);
      
      return distA - distB;
    });
    
    // Render objects front-to-back, using previously rendered objects as occluders
    const oldTarget = this.occlusionRenderer.getRenderTarget();
    this.occlusionRenderer.setRenderTarget(this.occlusionRenderTarget);
    
    // First, clear the render target
    this.occlusionRenderer.clear();
    
    // Add and test objects in front-to-back order
    for (const object of objects) {
      const id = object.getId();
      const box = this.getOcclusionBox(object);
      this.updateOcclusionBox(object);
      
      // In a real implementation, we would:
      // 1. Render the box with a unique color
      // 2. Read back pixels to see if any are visible
      // 3. If visible, mark the object as visible and add it to occluders
      
      // For this simplified implementation, we'll just mark all objects as visible
      // In a real game, you'd use hardware occlusion queries or compute the visibility
      this.visibleObjects.add(id);
      this.totalObjectsVisible++;
      
      // Add the object to the occlusion scene for future tests
      this.occlusionScene.add(box);
      
      // Re-render the occlusion scene with this new object
      this.occlusionRenderer.render(this.occlusionScene, this.occlusionCamera);
    }
    
    // Restore original render target
    this.occlusionRenderer.setRenderTarget(oldTarget);
  }
  
  /**
   * Return the last computed set of visible objects
   * Used when skipping occlusion testing for performance
   */
  private getLastVisibleObjects(objects: GameObject[]): GameObject[] {
    // If we haven't run occlusion testing yet, consider all objects visible
    if (this.visibleObjects.size === 0) {
      return objects;
    }
    
    // Otherwise filter based on the last computed visibility
    return objects.filter(obj => this.visibleObjects.has(obj.getId()));
  }
  
  /**
   * Update debug visualizations for the occlusion system
   */
  private updateDebugVisualizations(): void {
    // Clear previous debug objects
    this.clearDebugHelpers();
    
    // Show occlusion boxes for visible objects in green, occluded in red
    for (const [id, box] of this.occlusionBoxes.entries()) {
      const isVisible = this.visibleObjects.has(id);
      const isOccluded = this.occludedObjects.has(id);
      
      // Create a wireframe box to represent the occlusion box
      const geometry = new THREE.BoxGeometry(
        box.scale.x, 
        box.scale.y, 
        box.scale.z
      );
      
      const material = new THREE.MeshBasicMaterial({
        color: isVisible ? 0x00ff00 : 0xff0000,
        wireframe: true,
        transparent: true,
        opacity: 0.5
      });
      
      const debugBox = new THREE.Mesh(geometry, material);
      debugBox.position.copy(box.position);
      
      this.scene.add(debugBox);
      this.debugObjects.push(debugBox);
    }
  }
  
  /**
   * Clear all debug visualization objects
   * Private helper method that does the actual cleanup
   */
  private clearDebugHelpers(): void {
    for (const obj of this.debugObjects) {
      this.scene.remove(obj);
      if (obj instanceof THREE.Mesh) {
        obj.geometry.dispose();
        if (obj.material instanceof THREE.Material) {
          obj.material.dispose();
        } else if (Array.isArray(obj.material)) {
          for (const material of obj.material) {
            material.dispose();
          }
        }
      }
    }
    this.debugObjects = [];
  }
  
  /**
   * Check if an object is visible (not occluded)
   */
  public isObjectVisible(objectId: string): boolean {
    return this.visibleObjects.has(objectId);
  }
  
  /**
   * Check if an object is occluded
   */
  public isObjectOccluded(objectId: string): boolean {
    return this.occludedObjects.has(objectId);
  }
  
  /**
   * Enable or disable the occlusion culling system
   */
  public setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }
  
  /**
   * Enable or disable debug visualization
   */
  public setDebugMode(enabled: boolean): void {
    this.debugMode = enabled;
    
    if (!enabled) {
      this.clearDebugObjects();
    }
  }
  
  /**
   * Toggle debug mode
   */
  public toggleDebugMode(): void {
    this.setDebugMode(!this.debugMode);
  }
  
  /**
   * Set the interval for occlusion checks (in frames)
   */
  public setOcclusionCheckInterval(interval: number): void {
    this.occlusionCheckInterval = Math.max(1, interval);
  }
  
  /**
   * Enable or disable hierarchical occlusion testing
   */
  public setHierarchicalTesting(enabled: boolean): void {
    this.hierarchicalTesting = enabled;
  }
  
  /**
   * Get performance statistics
   */
  public getStats(): { 
    objectsChecked: number, 
    objectsVisible: number, 
    objectsOccluded: number 
  } {
    return {
      objectsChecked: this.totalObjectsChecked,
      objectsVisible: this.totalObjectsVisible,
      objectsOccluded: this.totalObjectsOccluded
    };
  }
  
  /**
   * Clear all debug visualization objects
   * Public method for external cleanup
   */
  public clearDebugObjects(): void {
    this.clearDebugHelpers();
  }
} 